/**
 * Timbrica — accessible game grid (shared primitive).
 *
 * Turns any board/grid game into something a screen-reader user can actually
 * play: every cell becomes a real focusable element with a spoken name, arrow
 * keys move a roving focus, and Enter/Space activates. The screen reader then
 * reads each square *natively on focus* — which is what makes this work on
 * Android TalkBack explore-by-touch too, where a live region alone says
 * nothing when the user drags a finger across the board.
 *
 * ⚠️ WHY NOT A LIVE REGION FOR NAVIGATION. /chess already had arrow-key board
 * navigation (chess-board.js) and it announced NOTHING: the cursor was a CSS
 * class, focus never left the board container, and the container's aria-label
 * never changed. Half the work existed and none of it reached the user. Focus
 * is the fix; the live region is only for things focus cannot express (the
 * opponent moved, check, a pair matched, the game ended).
 *
 *   var grid = window.__a11yGrid.create(boardEl, {
 *     name:  'Шахматная доска',
 *     cols:  8,
 *     cells: function(){ return visualOrderCellElements; },  // reading order!
 *     label: function(cell, i){ return 'e4, белая пешка'; },
 *     onActivate: function(i, cell){ ... },
 *   });
 *   grid.refresh();                 // after any state change — relabels
 *   grid.announce('Шах');           // event the focus cannot express
 *   grid.focus(i); grid.index(); grid.destroy();
 *
 * `cells` MUST return the cells in visual reading order (left→right, top→bottom
 * as the player sees them). The primitive physically re-parents them into
 * role="row" wrappers in that order, so DOM order == reading order == swipe
 * order. The wrappers are `display:contents`, so they generate no box and
 * neither CSS grid layout nor absolute positioning notices them — this is what
 * lets one primitive serve a CSS-grid board (memory-game) and an
 * absolutely-positioned one (chess) without either tool restyling anything.
 *
 * ⚠️⚠️ RE-PARENTING IS INVASIVE. Because the cells move into row wrappers, any
 * code that reaches them through the CONTAINER's own child list — `board
 * .children[i]`, `childNodes`, `:nth-child` — silently stops finding them: it
 * gets the rows instead. Nothing throws, the game just quietly stops updating
 * its cells. Four grid games in this repo do exactly that today (lights-out,
 * pipe-puzzle, woodblocks, flood-fill), so an adopter must switch to
 * `querySelectorAll('.your-cell')` first. Guard:
 * tests/Unit/A11yGridChildIndexingTest.php.
 *
 * Guard: tests/playwright/a11y-grid-speaks.mjs reads the real accessibility
 * tree (not the markup) — the markup is what the previous chess guard checked,
 * and it passed on a board that said nothing.
 */
(function (root) {
    'use strict';

    var SVG_NS = 'http://www.w3.org/2000/svg';

    var MODE_TOOL = 'a11y';        // shared bucket — the choice follows the user across tools
    var MODE_KEY = 'blind_mode';
    var modeListeners = [];

    /* ---------------------------------------------------------------- mode */

    function modeOn() {
        return document.documentElement.getAttribute('data-a11y-mode') === '1';
    }

    function setMode(on) {
        document.documentElement.setAttribute('data-a11y-mode', on ? '1' : '0');
        try {
            if (root.__toolSettings) root.__toolSettings.save(MODE_TOOL, MODE_KEY, !!on);
        } catch (e) {}
        for (var i = 0; i < modeListeners.length; i++) {
            try { modeListeners[i](!!on); } catch (e) {}
        }
    }

    function onModeChange(fn) {
        if (typeof fn === 'function') modeListeners.push(fn);
    }

    /* ------------------------------------------------------------ announce */

    var politeEl = null, assertiveEl = null, politeTimer = null;

    function region(assertive) {
        var id = assertive ? 'a11yGridAlert' : 'a11yGridStatus';
        var el = document.getElementById(id);
        if (el) return el;
        el = document.createElement('div');
        el.id = id;
        el.className = 'sr-only';
        el.setAttribute('role', assertive ? 'alert' : 'status');
        el.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
        el.setAttribute('aria-atomic', 'true');
        document.body.appendChild(el);
        return el;
    }

    /**
     * ⚠️ Identical consecutive text is not re-announced by any screen reader
     * (Lichess documents the same trap in its blind-mode guide). A game says
     * "Ваш ход" over and over, so we alternate an invisible suffix — a
     * word-joiner, which no reader speaks — to force a fresh announcement.
     */
    var flip = false;
    function announce(msg, assertive) {
        if (!msg) return;
        var el = assertive ? (assertiveEl = assertiveEl || region(true))
                           : (politeEl = politeEl || region(false));
        flip = !flip;
        var text = String(msg) + (flip ? '\u2060' : '');
        if (assertive) { el.textContent = text; return; }
        clearTimeout(politeTimer);
        el.textContent = '';
        politeTimer = setTimeout(function () { el.textContent = text; }, 40);
    }

    /* ---------------------------------------------------------------- grid */

    function create(gridEl, opts) {
        if (!gridEl) return null;
        opts = opts || {};

        var getCells = typeof opts.cells === 'function'
            ? opts.cells
            : function () { return Array.prototype.slice.call(gridEl.children); };
        var labelFor = opts.label || function (cell) { return cell.getAttribute('aria-label') || ''; };
        var roleFor = opts.role || function () { return 'gridcell'; };
        var onActivate = opts.onActivate || function () {};
        var colsOf = typeof opts.cols === 'function' ? opts.cols : function () { return opts.cols || 1; };
        var wrapEdges = opts.wrap === true;

        /* Two archetypes, and picking the wrong one breaks the game.
           `navigate: true` (default) — "land on a cell and act on it": chess,
           memory, lights-out. Arrows move a roving focus.
           `navigate: false` — "the arrows ARE the move": 2048 and every other
           whole-board game. Taking the arrows there would make the game
           unplayable for exactly the person this is for, so the primitive only
           names the cells and builds the rows; the reader still walks the grid
           with its own table commands, and the tool reports what its move did. */
        var navigable = opts.navigate !== false;
        var isSvg = gridEl.namespaceURI === SVG_NS;

        var cells = [];
        var rowsWrap = [];
        var cursor = typeof opts.initialIndex === 'number' ? opts.initialIndex : 0;
        var destroyed = false;

        gridEl.setAttribute('role', 'grid');
        if (opts.name) gridEl.setAttribute('aria-label', opts.name);

        /**
         * Re-parent the cells into role="row" wrappers. A bare grid whose
         * gridcells are direct children is an invalid ARIA structure and
         * NVDA/VoiceOver read it unpredictably; `display:contents` gives us the
         * rows without giving them a box.
         */
        function buildRows() {
            var cols = Math.max(1, colsOf() | 0);
            var rows = Math.ceil(cells.length / cols);

            // Reuse wrappers when the shape did not change — rebuilding on every
            // refresh would blow focus away mid-game. ⚠️ "same count" is not
            // enough: a tool that rebuilds its board with innerHTML (memory-game
            // does, every new deal) detaches the wrappers while leaving this
            // array full, and appending cells into orphans renders an empty
            // board. Check that they are still OUR children.
            var stale = rowsWrap.length !== rows
                || (rowsWrap.length && rowsWrap[0].parentNode !== gridEl);
            if (stale) {
                for (var d = 0; d < rowsWrap.length; d++) {
                    if (rowsWrap[d].parentNode) rowsWrap[d].parentNode.removeChild(rowsWrap[d]);
                }
                rowsWrap = [];
                // Rows must land BEFORE whatever the tool paints over the cells
                // — chess stacks an absolutely-positioned piece layer on top of
                // the squares, and `display:contents` wrappers do not create a
                // stacking context, so appending them last would put the empty
                // squares over the pieces.
                var before = typeof opts.rowsBefore === 'function' ? opts.rowsBefore()
                           : (opts.rowsBefore || null);
                for (var r = 0; r < rows; r++) {
                    var w = makeRow();
                    rowsWrap.push(w);
                    if (before && before.parentNode === gridEl) gridEl.insertBefore(w, before);
                    else gridEl.appendChild(w);
                }
            }

            gridEl.setAttribute('aria-rowcount', String(rows));
            gridEl.setAttribute('aria-colcount', String(cols));

            for (var i = 0; i < cells.length; i++) {
                var cell = cells[i];
                var rIdx = Math.floor(i / cols), cIdx = i % cols;
                var target = rowsWrap[rIdx];
                if (cell.parentNode !== target) target.appendChild(cell);
                // Most cells are plain gridcells, but a puzzle can have real
                // headers — nonogram's row/column clues are exactly that, and
                // `rowheader`/`columnheader` make a screen reader read the clue
                // BY ITSELF when you cross into a new row or column, which is
                // the whole game there.
                cell.setAttribute('role', roleFor(cell, i));
                cell.setAttribute('aria-rowindex', String(rIdx + 1));
                cell.setAttribute('aria-colindex', String(cIdx + 1));
            }
        }

        /**
         * An SVG board gets a real `<g>`; an HTML one gets a div that generates
         * no box. Two shapes, one reason: the row must be invisible to layout.
         * `<g>` already is — children keep their own coordinates — so SVG needs
         * no `display:contents` and none of its caveats.
         */
        function makeRow() {
            if (isSvg) {
                var g = document.createElementNS(SVG_NS, 'g');
                g.setAttribute('class', 'a11y-grid-row');
                g.setAttribute('role', 'row');
                return g;
            }
            var w = document.createElement('div');
            w.className = 'a11y-grid-row';
            w.setAttribute('role', 'row');
            w.style.display = 'contents';
            return w;
        }

        /* SVGElement has no reliable `tabIndex` setter across engines. */
        function setTab(el, v) {
            if (isSvg) el.setAttribute('tabindex', String(v));
            else el.tabIndex = v;
        }

        function relabel() {
            for (var i = 0; i < cells.length; i++) {
                var text = labelFor(cells[i], i, { mode: modeOn() });
                if (text) cells[i].setAttribute('aria-label', text);
            }
        }

        function applyTabIndex() {
            if (!navigable) return;
            if (cursor < 0 || cursor >= cells.length) cursor = 0;
            for (var i = 0; i < cells.length; i++) {
                setTab(cells[i], (i === cursor) ? 0 : -1);
            }
        }

        function refresh() {
            if (destroyed) return;
            cells = (getCells() || []).filter(Boolean);
            buildRows();
            relabel();
            applyTabIndex();
        }

        function focusCell(i, silent) {
            if (destroyed || !cells.length) return;
            cursor = Math.max(0, Math.min(cells.length - 1, i));
            applyTabIndex();
            var el = cells[cursor];
            if (!el) return;
            // preventScroll + block:'nearest' — never yank the page (global rule).
            try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
            try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) {}
            if (!silent && opts.onFocusCell) opts.onFocusCell(cursor, el);
        }

        function move(dRow, dCol) {
            var cols = Math.max(1, colsOf() | 0);
            var rows = Math.ceil(cells.length / cols);
            var r = Math.floor(cursor / cols), c = cursor % cols;
            r += dRow; c += dCol;
            if (wrapEdges) {
                r = (r + rows) % rows;
                c = (c + cols) % cols;
            } else {
                r = Math.max(0, Math.min(rows - 1, r));
                c = Math.max(0, Math.min(cols - 1, c));
            }
            var next = r * cols + c;
            if (next >= 0 && next < cells.length) focusCell(next);
        }

        function onKeyDown(ev) {
            if (destroyed || ev.altKey || ev.metaKey) return;
            var cols = Math.max(1, colsOf() | 0);
            var handled = true;
            switch (ev.key) {
                case 'ArrowLeft':  move(0, -1); break;
                case 'ArrowRight': move(0, 1); break;
                case 'ArrowUp':    move(-1, 0); break;
                case 'ArrowDown':  move(1, 0); break;
                case 'Home':
                    if (ev.ctrlKey) focusCell(0);
                    else focusCell(Math.floor(cursor / cols) * cols);
                    break;
                case 'End':
                    if (ev.ctrlKey) focusCell(cells.length - 1);
                    else focusCell(Math.min(cells.length - 1, Math.floor(cursor / cols) * cols + cols - 1));
                    break;
                case 'Enter':
                case ' ':
                    onActivate(cursor, cells[cursor], ev);
                    break;
                default: handled = false;
            }
            if (handled) { ev.preventDefault(); ev.stopPropagation(); }
        }

        // Keep the cursor in step with whatever the user actually focused —
        // TalkBack explore-by-touch and a plain Tab both land on a cell without
        // going through move().
        function onFocusIn(ev) {
            var idx = cells.indexOf(ev.target);
            if (idx >= 0 && idx !== cursor) { cursor = idx; applyTabIndex(); }
        }

        if (navigable) {
            gridEl.addEventListener('keydown', onKeyDown);
            gridEl.addEventListener('focusin', onFocusIn);

            // The board container itself must not be a tab stop any more: the
            // cells are. Leaving tabIndex=0 on it makes Tab land on a thing that
            // says nothing useful, which is where /chess and /tic-tac-toe were.
            if (gridEl.getAttribute('tabindex') === '0') setTab(gridEl, -1);
        }

        refresh();

        var api = {
            refresh: refresh,
            relabel: relabel,
            focus: focusCell,
            index: function () { return cursor; },
            setIndex: function (i) { cursor = i; applyTabIndex(); },
            cells: function () { return cells.slice(); },
            announce: announce,
            destroy: function () {
                destroyed = true;
                gridEl.removeEventListener('keydown', onKeyDown);
                gridEl.removeEventListener('focusin', onFocusIn);
            }
        };
        return api;
    }

    root.__a11yGrid = {
        create: create,
        announce: announce,
        mode: modeOn,
        setMode: setMode,
        onModeChange: onModeChange
    };
})(window);
